import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, ArrowRight, Clock, RefreshCw, Search } from 'lucide-react';
import type { DueReminderLoanItem } from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { formatDate } from '@/lib/format';
import { ErrorState, Modal, Money, Spinner, StatusBadge } from './ui';

type FilterStage = 'ALL' | 'OVERDUE' | 'DUE_SOON';

/** The overdue and due-soon invoices, and loan EMIs, behind the banner, loaded when it is opened. */
export function DueReminderModal({ onClose }: { onClose(): void }) {
  const { companyId, can } = useAuth();
  const [filter, setFilter] = useState<FilterStage>('ALL');
  const [search, setSearch] = useState('');

  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['due-reminders', companyId, 'items'],
    queryFn: () => api.invoices.dueReminders({ companyId, withItems: 'true' }),
    enabled: can('invoice:read'),
  });
  const loans = useQuery({
    queryKey: ['due-reminders', companyId, 'loans'],
    queryFn: () => api.loans.dueReminders({ companyId }),
    enabled: can('loan:read'),
  });

  const filteredItems = useMemo(() => {
    let list = data?.items ?? [];
    if (filter !== 'ALL') list = list.filter((i) => i.urgency === filter);

    const query = search.trim().toLowerCase();
    if (query) {
      list = list.filter(
        (i) =>
          i.trackingId.toLowerCase().includes(query) ||
          (i.invoiceNumber && i.invoiceNumber.toLowerCase().includes(query)) ||
          (i.vendorName && i.vendorName.toLowerCase().includes(query)),
      );
    }
    return list;
  }, [data?.items, filter, search]);

  const filteredLoans = useMemo(() => {
    let list = loans.data?.items ?? [];
    if (filter !== 'ALL') list = list.filter((i) => i.urgency === filter);
    const query = search.trim().toLowerCase();
    if (query) {
      list = list.filter(
        (i) =>
          i.lenderName.toLowerCase().includes(query) ||
          i.loanReference.toLowerCase().includes(query),
      );
    }
    return list;
  }, [loans.data?.items, filter, search]);

  const invoiceCounts = data?.counts ?? { overdue: 0, dueSoon: 0, total: 0 };
  const loanCounts = loans.data?.counts ?? { overdue: 0, dueSoon: 0, total: 0 };
  const counts = {
    overdue: invoiceCounts.overdue + loanCounts.overdue,
    dueSoon: invoiceCounts.dueSoon + loanCounts.dueSoon,
    total: invoiceCounts.total + loanCounts.total,
  };
  const reminderDays = Math.max(data?.reminderDays ?? 0, loans.data?.reminderDays ?? 0);
  const truncated = !!data && data.items.length < invoiceCounts.total;

  return (
    <Modal
      title="Due Date Reminders"
      wide
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto self-center text-xs text-ink-500">
            Configured under System Settings. Excludes paid or settled invoices.
          </span>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="btn-secondary flex items-center gap-1.5"
            aria-label="Refresh invoice reminders"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Refresh
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </>
      }
    >
      {/* Filters stay in view while the list scrolls beneath them. */}
      <div className="sticky -top-4 z-10 -mx-5 -mt-4 flex flex-col gap-3 border-b border-ink-100 bg-white px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Invoice urgency filter">
          <FilterTab active={filter === 'ALL'} onClick={() => setFilter('ALL')} tone="all">
            All ({counts.total})
          </FilterTab>
          <FilterTab
            active={filter === 'OVERDUE'}
            onClick={() => setFilter('OVERDUE')}
            tone="overdue"
          >
            <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
            Overdue ({counts.overdue})
          </FilterTab>
          <FilterTab
            active={filter === 'DUE_SOON'}
            onClick={() => setFilter('DUE_SOON')}
            tone="soon"
          >
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Due Soon ({counts.dueSoon})
          </FilterTab>
        </div>

        <div className="relative w-full sm:w-64">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400"
            aria-hidden="true"
          />
          <input
            type="search"
            placeholder="Search invoice or vendor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input h-9 pl-9 text-xs"
            aria-label="Filter invoices"
          />
        </div>
      </div>

      {reminderDays > 0 ? (
        <p className="mt-3 text-xs text-ink-500">
          Overdue, or due within {reminderDays} days.
          {truncated
            ? ` Showing the ${data!.items.length} most urgent of ${invoiceCounts.total} invoices.`
            : null}
        </p>
      ) : null}

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <div className="py-4">
          <ErrorState error={error} />
        </div>
      ) : filteredItems.length === 0 && filteredLoans.length === 0 ? (
        <div className="py-12 text-center">
          <Clock className="mx-auto h-8 w-8 text-ink-300" aria-hidden="true" />
          <p className="mt-2 text-sm font-medium text-ink-800">No matching invoices</p>
          <p className="mt-1 text-xs text-ink-500">
            {search ? `No records matched “${search}”.` : 'No invoices in this view.'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-ink-100">
          {filteredLoans.map((item) => (
            <LoanRow key={item.id} item={item} onClose={onClose} />
          ))}
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="flex flex-col gap-2 py-3.5 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/invoices/${item.id}`}
                    onClick={onClose}
                    className="truncate text-sm font-semibold text-brand-700 hover:underline"
                  >
                    {item.invoiceNumber || item.trackingId}
                  </Link>
                  {item.invoiceNumber && item.trackingId !== item.invoiceNumber ? (
                    <span className="text-xs text-ink-400">({item.trackingId})</span>
                  ) : null}
                  <UrgencyBadge item={item} />
                </div>
                <p className="mt-0.5 truncate text-xs text-ink-600">
                  {item.vendorName || 'Vendor not specified'} · Due {formatDate(item.dueDate)}
                </p>
              </div>

              <div className="flex shrink-0 items-center justify-between gap-4 sm:justify-end">
                <div className="text-right">
                  <p className="text-sm font-semibold tabular-nums text-ink-900">
                    <Money
                      minor={item.netPayable ?? item.totalAmount ?? 0}
                      currency={item.currency}
                    />
                  </p>
                  <div className="mt-0.5">
                    <StatusBadge status={item.status} />
                  </div>
                </div>

                <Link
                  to={`/invoices/${item.id}`}
                  onClick={onClose}
                  className="btn-icon h-8 w-8 text-ink-400 hover:bg-brand-50 hover:text-brand-700"
                  title="Open invoice details"
                  aria-label={`Open details for invoice ${item.invoiceNumber || item.trackingId}`}
                >
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

const TAB_TONES = {
  all: [
    'bg-ink-900 text-white shadow-sm',
    'border border-ink-200 bg-white text-ink-600 hover:bg-ink-100',
  ],
  overdue: [
    'bg-rose-700 text-white shadow-sm',
    'border border-rose-200 bg-white text-rose-700 hover:bg-rose-50',
  ],
  soon: [
    'bg-amber-600 text-white shadow-sm',
    'border border-amber-200 bg-white text-amber-800 hover:bg-amber-50',
  ],
} as const;

function FilterTab({
  active,
  onClick,
  tone,
  children,
}: {
  active: boolean;
  onClick(): void;
  tone: keyof typeof TAB_TONES;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
        TAB_TONES[tone][active ? 0 : 1]
      }`}
    >
      {children}
    </button>
  );
}

/** A loan EMI due or overdue; it opens the loan's track in Payment Accounts. */
function LoanRow({ item, onClose }: { item: DueReminderLoanItem; onClose(): void }) {
  const href = `/settings/payment-accounts/loans?loan=${item.loanId}`;
  return (
    <div className="flex flex-col gap-2 py-3.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={href}
            onClick={onClose}
            className="truncate text-sm font-semibold text-brand-700 hover:underline"
          >
            {item.loanReference} · EMI {item.n}
          </Link>
          <UrgencyBadge item={item} />
        </div>
        <p className="mt-0.5 truncate text-xs text-ink-600">
          Loan EMI · {item.lenderName} · Due {formatDate(item.dueDate)}
        </p>
      </div>
      <div className="flex shrink-0 items-center justify-between gap-4 sm:justify-end">
        <p className="text-sm font-semibold tabular-nums text-ink-900">
          <Money minor={item.amount} />
        </p>
        <Link
          to={href}
          onClick={onClose}
          className="btn-icon h-8 w-8 text-ink-400 hover:bg-brand-50 hover:text-brand-700"
          title="Open the loan"
          aria-label={`Open loan ${item.loanReference}`}
        >
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

function UrgencyBadge({
  item,
}: {
  item: { urgency: 'OVERDUE' | 'DUE_SOON'; daysDifference: number };
}) {
  if (item.urgency === 'OVERDUE') {
    const days = Math.abs(item.daysDifference);
    return (
      <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
        {days === 1 ? '1 day overdue' : `${days}d overdue`}
      </span>
    );
  }
  const days = item.daysDifference;
  return (
    <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
      {days === 0 ? 'Due today' : days === 1 ? 'Due tomorrow' : `Due in ${days}d`}
    </span>
  );
}
